import { flip, useMergeRefs } from '@floating-ui/react';
import * as React from 'react';
import { useSlottedContext } from 'react-aria-components';

import { assert } from '@gitbook/util-ts';

import { OverlayRoot } from '../Overlay';
import { PopoverContext } from '../Popover';
import { AutoCompleteStateContext } from './AutoComplete';
import { MenuContext } from './Menu';
import { MenuItemContext } from './MenuItem';
import { getMenuFloatingMiddleware, menuPopoverProps } from './MenuPopover';
import { MenuStateContext } from './MenuState';

export type SubMenuRef = {
    focus: () => void;
    isPointerInGraceArea: (
        event: React.PointerEvent,
        direction: 'left' | 'right' | null,
        onExpired: () => void
    ) => void;
};

type SubMenuContextType = {
    id: string;
    ref: React.RefObject<SubMenuRef> | undefined;
    onClose: () => void;
    isOpen: boolean;
    animate: boolean;
};

export const SubMenuContext = React.createContext<SubMenuContextType | null>(null);

export type SubMenuProps = { children: React.ReactNode };

export function SubMenu(props: SubMenuProps) {
    const context = React.useContext(SubMenuContext);
    const menuState = React.useContext(MenuStateContext);
    const menuItemCtx = useSlottedContext(MenuItemContext);
    assert(context && menuState && menuItemCtx, 'SubMenu must be used in a Menu');
    const triggerRef = React.useRef<HTMLDivElement>(null);
    const menuRef = React.useRef<HTMLDivElement>(null);
    const { setFocusedId, focusedId } = menuState;
    const pointerGraceTimerRef = React.useRef(0);
    const pointerGraceIntentRef = React.useRef<{ area: Polygon; side: 'left' | 'right' } | null>(
        null
    );
    const pointerGraceExpiredCallbackRef = React.useRef<(() => void) | null>(null);
    const isFocused = focusedId === context.id;
    React.useEffect(() => {
        return () => {
            window.clearTimeout(pointerGraceTimerRef.current);
            pointerGraceIntentRef.current = null;
        };
    }, []);
    React.useImperativeHandle(context?.ref, () => {
        return {
            focus: () => setFocusedId(context.id),
            isPointerInGraceArea: (event, direction, onExpired) => {
                if (
                    pointerGraceIntentRef.current &&
                    pointerGraceIntentRef.current.side === direction &&
                    isPointerInGraceArea(event, pointerGraceIntentRef.current.area)
                ) {
                    pointerGraceExpiredCallbackRef.current = onExpired;
                    return true;
                }
                return false;
            },
        };
    }, [context.id, setFocusedId]);
    const mergedRef = useMergeRefs([triggerRef, menuItemCtx?.ref]);
    const [alignmentAxisOffset, setAlignmentAxisOffset] = React.useState(0);
    const popoverRefCallback = React.useCallback((element: HTMLDivElement | null) => {
        if (element) {
            const search = element.querySelector('[data-menu-search][data-hidden=false]');
            if (search) {
                setAlignmentAxisOffset(-search.getBoundingClientRect().height);
            }
        }
    }, []);
    return (
        <OverlayRoot
            isOpen={context.isOpen ?? false}
            onOpenChange={(open) => {
                if (!open) {
                    context.onClose();
                }
            }}
            triggerRef={triggerRef}
        >
            <PopoverContext.Provider
                value={{
                    ...menuPopoverProps,
                    ref: popoverRefCallback,
                    floating: {
                        middleware: [
                            ...getMenuFloatingMiddleware({
                                offset: {
                                    mainAxis: -1,
                                    alignmentAxis: alignmentAxisOffset,
                                },
                            }),
                            flip({
                                padding: 16,
                                mainAxis: true,
                                crossAxis: false,
                            }),
                        ],
                    },
                    placement: 'right-start',
                    offset: -1,
                    animate: context.animate,
                    restoreFocus: false,
                    contain: isFocused,
                    shouldCloseOnInteractOutside: (element) => {
                        // We never close if we click inside a menu.
                        return !element.closest('[data-spine-popover]:has([data-spine-menu])');
                    },
                }}
            >
                <AutoCompleteStateContext.Provider value={null}>
                    <MenuContext.Provider value={{ id: context.id, ref: menuRef }}>
                        <MenuItemContext.Provider
                            value={{
                                ...menuItemCtx,
                                ref: mergedRef,
                                subMenu: isFocused ? 'focused' : context.isOpen ? 'open' : 'closed',
                                onPointerLeave: (event) => {
                                    if (menuRef.current) {
                                        const popover =
                                            menuRef.current.closest('[data-spine-popover]');
                                        assert(popover, 'A menu must be displayed in a popover');
                                        const contentRect = popover.getBoundingClientRect();
                                        const itemRect =
                                            event.currentTarget.getBoundingClientRect();
                                        const isRightSide = contentRect.left > itemRect.left;
                                        const bleed = isRightSide ? -5 : 5;
                                        const contentNearEdge =
                                            contentRect[isRightSide ? 'left' : 'right'];
                                        const contentFarEdge =
                                            contentRect[isRightSide ? 'right' : 'left'];
                                        const area = [
                                            // Apply a bleed on clientX to ensure that our exit point is
                                            // consistently within polygon bounds
                                            { x: event.clientX + bleed, y: event.clientY },
                                            { x: contentNearEdge, y: contentRect.top },
                                            { x: contentFarEdge, y: contentRect.top },
                                            { x: contentFarEdge, y: contentRect.bottom },
                                            { x: contentNearEdge, y: contentRect.bottom },
                                        ];
                                        window.clearTimeout(pointerGraceTimerRef.current);
                                        pointerGraceIntentRef.current = {
                                            side: isRightSide ? 'right' : 'left',
                                            area,
                                        };
                                        pointerGraceTimerRef.current = window.setTimeout(() => {
                                            pointerGraceIntentRef.current = null;
                                            if (pointerGraceExpiredCallbackRef.current) {
                                                pointerGraceExpiredCallbackRef.current();
                                            }
                                        }, 300);
                                    } else {
                                        window.clearTimeout(pointerGraceTimerRef.current);
                                        pointerGraceIntentRef.current = null;
                                    }
                                },
                            }}
                        >
                            {props.children}
                        </MenuItemContext.Provider>
                    </MenuContext.Provider>
                </AutoCompleteStateContext.Provider>
            </PopoverContext.Provider>
        </OverlayRoot>
    );
}

type Point = { x: number; y: number };
type Polygon = Point[];

function isPointerInGraceArea(event: React.PointerEvent, area: Polygon) {
    const cursorPos = { x: event.clientX, y: event.clientY };
    return isPointInPolygon(cursorPos, area);
}

function isPointInPolygon(point: Point, polygon: Polygon) {
    const { x, y } = point;
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const ii = polygon[i]!;
        const jj = polygon[j]!;
        const xi = ii.x;
        const yi = ii.y;
        const xj = jj.x;
        const yj = jj.y;

        // prettier-ignore
        const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
        if (intersect) inside = !inside;
    }

    return inside;
}
